/**
 * useMarkerTracking — Combined hook for camera + detection + API lookup.
 *
 * This is the main hook that scanner pages consume. It:
 * 1. Manages camera via useCamera
 * 2. Runs ArUco + QR detection via useDetection
 * 3. Tracks markers with persistence (keeps recently-seen markers visible)
 * 4. Resolves detected markers to boxes via batch-lookup API
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useCamera } from '../camera/useCamera';
import { useDetection } from '../detection/useDetection';
import { batchLookup } from '../api/boxes';
import { markerKey } from '../detection/types';
import { trackEvent } from '@shared/plausible';
import type { BoxWithItems, MarkerRef, MarkerType } from '@shared/types';
import type { TrackedMarker, UseMarkerTrackingReturn } from '../detection/types';

interface UseMarkerTrackingOptions {
  /** How long to keep markers after last detection (ms). Default: 3000 */
  persistMs?: number;
  /** Debounce interval for batch-lookup API calls (ms). Default: 500 */
  batchLookupDebounceMs?: number;
  /** Enable ArUco detection. Default: true */
  enableAruco?: boolean;
  /** Enable QR detection. Default: true */
  enableQr?: boolean;
}

const DEFAULT_PERSIST_MS = 3000;
const DEFAULT_BATCH_DEBOUNCE_MS = 500;

/** Sort tracked markers: ArUco first (numeric), then QR (alphabetical) */
function sortTrackedMarkers(markers: TrackedMarker[]): TrackedMarker[] {
  return markers.sort((a, b) => {
    // ArUco before QR
    if (a.markerType !== b.markerType) {
      return a.markerType === 'aruco' ? -1 : 1;
    }
    // Within same type: ArUco by numeric ID, QR alphabetical
    if (a.markerType === 'aruco') {
      return parseInt(a.markerId, 10) - parseInt(b.markerId, 10);
    }
    return a.markerId.localeCompare(b.markerId);
  });
}

export function useMarkerTracking(options: UseMarkerTrackingOptions = {}): UseMarkerTrackingReturn {
  const {
    persistMs = DEFAULT_PERSIST_MS,
    batchLookupDebounceMs = DEFAULT_BATCH_DEBOUNCE_MS,
    enableAruco = true,
    enableQr = true,
  } = options;

  const camera = useCamera({ facingMode: 'environment' });
  const detection = useDetection(camera.videoRef, {
    enabled: camera.status === 'active',
    enableAruco,
    enableQr,
  });

  // Tracked markers map: "markerType:markerId" -> TrackedMarker
  const trackedRef = useRef<Map<string, TrackedMarker>>(new Map());
  const [trackedMarkers, setTrackedMarkers] = useState<TrackedMarker[]>([]);

  // Box cache: "markerType:markerId" -> BoxWithItems (persists for session)
  const boxCacheRef = useRef<Map<string, BoxWithItems>>(new Map());

  // Debounce timer for batch lookups
  const lookupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingLookupKeysRef = useRef<Set<string>>(new Set());
  // Map key -> MarkerRef for pending lookups
  const pendingMarkerRefsRef = useRef<Map<string, MarkerRef>>(new Map());

  // Batch lookup for new markers
  const scheduleBatchLookup = useCallback(
    (newMarkers: Array<{ key: string; markerType: MarkerType; markerId: string }>) => {
      for (const m of newMarkers) {
        if (!boxCacheRef.current.has(m.key)) {
          pendingLookupKeysRef.current.add(m.key);
          pendingMarkerRefsRef.current.set(m.key, { type: m.markerType, id: m.markerId });
        }
      }

      if (pendingLookupKeysRef.current.size === 0) return;

      if (lookupTimerRef.current) {
        clearTimeout(lookupTimerRef.current);
      }

      lookupTimerRef.current = setTimeout(async () => {
        const keys = Array.from(pendingLookupKeysRef.current);
        const refs = keys
          .map((k) => pendingMarkerRefsRef.current.get(k))
          .filter((r): r is MarkerRef => r !== undefined);
        pendingLookupKeysRef.current.clear();
        pendingMarkerRefsRef.current.clear();

        if (refs.length === 0) return;

        try {
          const boxes = await batchLookup(refs);

          // Cache results
          for (const box of boxes) {
            const key = `${box.markerType}:${box.markerId}`;
            boxCacheRef.current.set(key, box);
          }

          // Update tracked markers with box data
          const tracked = trackedRef.current;
          let changed = false;
          for (const box of boxes) {
            const key = `${box.markerType}:${box.markerId}`;
            const existing = tracked.get(key);
            if (existing && !existing.box) {
              existing.box = box;
              changed = true;
            }
          }
          if (changed) {
            setTrackedMarkers(sortTrackedMarkers(Array.from(tracked.values())));
          }
        } catch {
          // Batch lookup failed — markers remain without box data
        }
      }, batchLookupDebounceMs);
    },
    [batchLookupDebounceMs],
  );

  // Process detection results
  useEffect(() => {
    if (!detection.detections) return;

    const now = performance.now();
    const tracked = trackedRef.current;
    const newMarkers: Array<{ key: string; markerType: MarkerType; markerId: string }> = [];
    let hasNewDetection = false;

    // Update/add detected markers
    for (const marker of detection.detections.markers) {
      const key = markerKey(marker);
      const existing = tracked.get(key);
      if (existing) {
        existing.corners = marker.corners;
        existing.lastSeen = now;
      } else {
        const cachedBox = boxCacheRef.current.get(key);
        tracked.set(key, {
          ...marker,
          firstSeen: now,
          lastSeen: now,
          box: cachedBox,
        });
        hasNewDetection = true;
        if (!cachedBox) {
          newMarkers.push({ key, markerType: marker.markerType, markerId: marker.markerId });
        }
      }
    }

    // Track scan event when new markers are detected
    if (hasNewDetection) {
      trackEvent('Scan Performed', { markerCount: detection.detections.markers.length });
    }

    // Remove stale markers
    for (const [key, marker] of tracked) {
      if (now - marker.lastSeen > persistMs) {
        tracked.delete(key);
      }
    }

    setTrackedMarkers(sortTrackedMarkers(Array.from(tracked.values())));

    if (newMarkers.length > 0) {
      scheduleBatchLookup(newMarkers);
    }
  }, [detection.detections, persistMs, scheduleBatchLookup]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (lookupTimerRef.current) {
        clearTimeout(lookupTimerRef.current);
      }
    };
  }, []);

  return {
    trackedMarkers,
    cameraStatus: camera.status,
    cameraError: camera.error,
    startCamera: camera.start,
    stopCamera: camera.stop,
    isDetecting: detection.isDetecting,
    fps: detection.fps,
  };
}
